import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { path_url } from "../../Config/config";
import { FaFileInvoiceDollar } from "react-icons/fa";

const quotationSchema = Yup.object().shape({
  subject: Yup.string().required("Subject is required"),
  amount: Yup.number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(0, "Amount must be 0 or more"),
  duedate: Yup.string().required("Valid until date is required"),
  status: Yup.string().required("Status is required"),
  notes: Yup.string(),
});

const StatusOptions = [
  "Draft",
  "Sent",
  "Accepted",
  "Rejected",
  "Expired",
  "Closed",
];

export default function CreateQuotationModal({
  open,
  onClose,
  leadId,
  onQuotationCreated,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div
          className="d-flex align-items-center gap-2"
          style={{ color: "#8b5cf6", fontWeight: 600 }}
        >
          <FaFileInvoiceDollar style={{ fontSize: "1.25rem" }} />
          <span>Create Quotation</span>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="pt-4">
        <Formik
          initialValues={{
            subject: "",
            amount: "",
            duedate: new Date().toISOString().split("T")[0],
            status: "Draft",
            notes: "",
          }}
          validationSchema={quotationSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const token = localStorage.getItem("accessToken");
              const adminId = localStorage.getItem("adminId");
              const payload = {
                ...values,
                leadid: leadId,
                useradminid: adminId,
                usercid: adminId,
              };

              let response;
              try {
                response = await axios.post(
                  `${path_url}/quotation`,
                  payload,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
              } catch (err) {
                // Fallback: if /quotation endpoint doesn't exist yet, use /task as a bridge
                if (err.response && err.response.status === 404) {
                  response = await axios.post(`${path_url}/task`, payload, {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  });
                } else {
                  throw err;
                }
              }

              if (
                response.data?.statusCodeValue === 200 ||
                response.status === 200 ||
                response.status === 201
              ) {
                toast.success("Quotation created successfully!");
                const createdQuotation = response.data?.body || payload;
                if (onQuotationCreated) {
                  onQuotationCreated(createdQuotation);
                }
                resetForm();
                onClose();
              } else {
                toast.error(
                  response.data?.body || "Failed to create quotation"
                );
              }
            } catch (error) {
              console.error("Quotation creation error:", error);
              toast.error(
                error.response?.data?.message ||
                  error.message ||
                  "Failed to create quotation"
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit} className="row g-3 mt-1">
              {/* Subject */}
              <div className="col-md-8 mb-3">
                <label className="form-label font-weight-bold">
                  Subject / Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  className={`form-control ${
                    touched.subject && errors.subject ? "is-invalid" : ""
                  }`}
                  placeholder="e.g. Annual Software License Quote"
                  value={values.subject}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.subject && errors.subject && (
                  <div className="invalid-feedback">{errors.subject}</div>
                )}
              </div>

              {/* Amount */}
              <div className="col-md-4 mb-3">
                <label className="form-label font-weight-bold">
                  Amount (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  name="amount"
                  min="0"
                  step="0.01"
                  className={`form-control ${
                    touched.amount && errors.amount ? "is-invalid" : ""
                  }`}
                  placeholder="0.00"
                  value={values.amount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.amount && errors.amount && (
                  <div className="invalid-feedback">{errors.amount}</div>
                )}
              </div>

              {/* Valid Until / Due Date */}
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Valid Until <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  name="duedate"
                  className={`form-control ${
                    touched.duedate && errors.duedate ? "is-invalid" : ""
                  }`}
                  value={values.duedate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.duedate && errors.duedate && (
                  <div className="invalid-feedback">{errors.duedate}</div>
                )}
              </div>

              {/* Status */}
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Status <span className="text-danger">*</span>
                </label>
                <select
                  name="status"
                  className={`form-select ${
                    touched.status && errors.status ? "is-invalid" : ""
                  }`}
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {StatusOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.status && errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}
              </div>

              {/* Notes */}
              <div className="col-md-12 mb-3">
                <label className="form-label font-weight-bold">Notes</label>
                <textarea
                  name="notes"
                  rows={3}
                  className="form-control"
                  placeholder="Additional details, terms, or conditions..."
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              {/* Actions */}
              <div className="col-12 d-flex justify-content-end gap-2 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn px-4 text-white"
                  disabled={isSubmitting}
                  style={{ backgroundColor: "#8b5cf6", borderColor: "#8b5cf6" }}
                >
                  {isSubmitting ? "Saving..." : "Create Quotation"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
